import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { AdminRole, FundWallet, JustLendRental, JustLendView, PoolLink } from "../api/types.js";
import { MutationForm, fieldError } from "../forms/mutation-form.js";
import { count, sunAmount, trx, utc } from "../format.js";
import {
  Address,
  Blank,
  Button,
  EmptyState,
  ErrorNote,
  Field,
  Note,
  Panel,
  Pill,
  ScreenHeader,
  ShortId,
  Spinner,
  StatRow,
  Table,
  Td,
  Th,
  Tile,
  TxId,
  inputClass,
} from "../ui.js";

/**
 * Where the energy comes from: the fund's wallets, the pool links that keep their meters
 * clear, the providers (`GET /v1/admin/providers`) and JustLend (`GET /v1/admin/justlend`).
 *
 * THE CAPACITY CARDS ARE SUMS OF THE ROWS BELOW, and nothing else: with no wallet registered
 * they say so instead of showing a total of zero.
 *
 * MUTATION CONTROLS ARE NOT RENDERED FOR A VIEWER. Not disabled — absent. A disabled button is
 * a promise that pressing it would work, and a viewer pressing it would get a 403 from the
 * server anyway; showing it is telling somebody about a door they cannot open.
 */
export function SupplyScreen({ role }: { role: AdminRole }) {
  const wallets = useResource((signal) => {
    void signal;
    return adminReads.fundWallets();
  }, []);
  const links = useResource((signal) => {
    void signal;
    return adminReads.poolLinks();
  }, []);
  const justLend = useResource((signal) => {
    void signal;
    return adminReads.justLend();
  }, []);

  const walletRows = wallets.data?.data ?? null;
  const live = walletRows === null || walletRows.length === 0 ? null : walletRows;
  const sum = (pick: (wallet: FundWallet) => number) =>
    live === null ? null : live.reduce((total, wallet) => total + pick(wallet), 0);
  const noWallet = walletRows === null ? "loading…" : "No fund wallet is registered yet.";

  return (
    <>
      <ScreenHeader
        title="Supply"
        lead="Fund wallets as the reconciler last read them from chain, and the pool links of docs/supply-pool-mode.md. Pool mode is off until a link exists."
      />

      <StatRow>
        <Tile
          icon="wallet"
          label="Fund wallets"
          value={live === null ? null : String(live.length)}
          note={
            live === null
              ? noWallet
              : `${live.filter((wallet) => wallet.status === "active").length} active, ${live.filter((wallet) => wallet.drift_strikes > 0).length} drifting`
          }
        />
        <Tile
          icon="stack"
          label="Staked for energy"
          value={live === null ? null : trx(sum((wallet) => wallet.staked_energy_sun), 0)}
          note={live === null ? noWallet : "Sum of the last chain sync of every wallet."}
        />
        <Tile
          icon="bolt"
          label="Free to delegate"
          value={live === null ? null : trx(sum((wallet) => wallet.can_delegate_max_sun), 0)}
          tone="good"
          note={live === null ? noWallet : "canDelegateMaxSize, summed. What own capacity can still sell."}
        />
        <Tile
          icon="clock"
          label="JustLend overdue"
          value={justLend.data == null ? null : String(justLend.data.summary.overdue)}
          tone={justLend.data != null && justLend.data.summary.overdue > 0 ? "warning" : "neutral"}
          note={
            justLend.error !== null
              ? justLend.error.message
              : justLend.data == null
                ? "loading…"
                : `${justLend.data.summary.open} open rental(s), ${justLend.data.summary.unsettled_returns} refund(s) unread.`
          }
        />
      </StatRow>

      <div class="space-y-3">
        <Panel
          title="Fund wallets"
          subtitle="Stake, meter, free capacity and the mandate we hold on each. Figures are the last chain sync, not a live read."
          action={
            role === "editor" ? (
              <RegisterWalletButton onDone={() => wallets.reload()} />
            ) : undefined
          }
        >
          {wallets.loading ? (
            <Spinner />
          ) : wallets.error !== null ? (
            <ErrorNote error={wallets.error} onRetry={() => wallets.reload()} />
          ) : (
            <FundWalletTable rows={wallets.data?.data ?? []} />
          )}
        </Panel>

        <Panel
          title="Pool links"
          subtitle="pool → fleet loans and their flush cycle. A paused link stops the scheduler, not the chain: the loan stays where it is."
          action={
            role === "editor" ? <CreateLinkButton onDone={() => links.reload()} /> : undefined
          }
        >
          {links.loading ? (
            <Spinner />
          ) : links.error !== null ? (
            <ErrorNote error={links.error} onRetry={() => links.reload()} />
          ) : (
            <PoolLinkTable rows={links.data?.data ?? []} role={role} onChanged={() => links.reload()} />
          )}
        </Panel>

        <Panel
          title="Providers"
          subtitle="The first overflow leg: health, prepaid credit and the low-water mark that triggers a top-up."
        >
          <Note>
            Open <a class="text-accent-text" href="/providers">Providers</a> for credit, our 24 h spend, low-water
            marks, daily caps and the last top-up.
          </Note>
        </Panel>

        <Panel
          title="JustLend"
          subtitle="The supply of last resort. Rentals still out, returns past expiry, refunds not read yet. The deposit and the daily cap are worker settings and are not readable here."
        >
          {justLend.loading && justLend.data == null ? (
            <Spinner />
          ) : justLend.error !== null ? (
            <ErrorNote error={justLend.error} onRetry={() => justLend.reload()} />
          ) : justLend.data == null ? null : (
            <JustLendPanel view={justLend.data} />
          )}
        </Panel>
      </div>
    </>
  );
}

function FundWalletTable({ rows }: { rows: FundWallet[] }) {
  return (
    <Table
      rows={rows.length}
      empty="No fund wallet is registered. Until one is, the platform has no own capacity and every order overflows to a provider."
      head={
        <tr>
          <Th>Address</Th>
          <Th>Supplier</Th>
          <Th>Role</Th>
          <Th>Status</Th>
          <Th>Permission</Th>
          <Th numeric>Stake</Th>
          <Th numeric>Free to delegate</Th>
          <Th numeric>Delegated by us</Th>
          <Th numeric>Meter used / limit</Th>
          <Th numeric>Drift</Th>
          <Th>Last sync</Th>
        </tr>
      }
    >
      {rows.map((wallet) => (
        <tr key={wallet.address}>
          <Td mono>
            <Address value={wallet.address} />
            {wallet.label === null ? null : (
              <span class="ml-1 text-ink-muted">{wallet.label}</span>
            )}
          </Td>
          <Td>{wallet.supplier_id ?? <Blank title="No supplier id on this row" />}</Td>
          <Td>
            <Pill value={wallet.role} />
          </Td>
          <Td>
            <Pill value={wallet.status} />
          </Td>
          <Td>
            {wallet.permission_id === null ? (
              <Blank title="No permission recorded" />
            ) : (
              <span title={wallet.operations_hex ?? "operations not recorded"}>
                #{wallet.permission_id} {wallet.mandate_status ?? "unknown"}
              </span>
            )}
          </Td>
          <Td numeric>{trx(wallet.staked_energy_sun, 0)}</Td>
          <Td numeric>{trx(wallet.can_delegate_max_sun, 0)}</Td>
          <Td numeric>{trx(wallet.delegated_out_by_us_sun, 0)}</Td>
          <Td numeric>
            {count(wallet.energy_used)} / {count(wallet.energy_limit)}
          </Td>
          <Td numeric>{wallet.drift_strikes === 0 ? <Blank /> : count(wallet.drift_strikes)}</Td>
          <Td>{utc(wallet.last_chain_sync_at)}</Td>
        </tr>
      ))}
    </Table>
  );
}

function PoolLinkTable({
  rows,
  role,
  onChanged,
}: {
  rows: PoolLink[];
  role: AdminRole;
  onChanged(): void;
}) {
  const [pausing, setPausing] = useState<PoolLink | null>(null);

  return (
    <>
      <Table
        rows={rows.length}
        empty="No pool link. Pool mode is OFF until one exists — today's wallets are fleets with no pool behind them."
        head={
          <tr>
            <Th>Link</Th>
            <Th>Pool → fleet</Th>
            <Th numeric>Lent</Th>
            <Th>Operator</Th>
            <Th>State</Th>
            <Th>Status</Th>
            <Th numeric>Trigger</Th>
            <Th numeric>Min interval</Th>
            <Th numeric>Bandwidth spent / budget</Th>
            <Th numeric>Flushes</Th>
            <Th>Last flush</Th>
            {role === "editor" ? <Th>{""}</Th> : null}
          </tr>
        }
      >
        {rows.map((link) => (
          <tr key={link.id}>
            <Td mono>{link.id}</Td>
            <Td mono>
              <Address value={link.pool_wallet} /> <span aria-hidden="true">→</span>{" "}
              <Address value={link.fleet_wallet} />
            </Td>
            <Td numeric>{sunAmount(link.lent_sun)}</Td>
            <Td>{link.operator}</Td>
            <Td>
              <Pill value={link.state} />
            </Td>
            <Td>
              <Pill value={link.status} />
            </Td>
            <Td numeric>{link.trigger_locked_bps} bps</Td>
            <Td numeric>{count(link.min_interval_s)} s</Td>
            <Td numeric>
              {count(link.bandwidth_spent)} / {count(link.daily_bandwidth_budget)}
              {link.bandwidth_day === null ? null : (
                <span class="ml-1 text-ink-muted">{link.bandwidth_day}</span>
              )}
            </Td>
            <Td numeric>{count(link.flush_count)}</Td>
            <Td>
              {utc(link.last_flush_at)}
              {link.last_error === null ? null : (
                <span class="ml-1 text-danger" title={link.last_error}>
                  error
                </span>
              )}
            </Td>
            {role === "editor" ? (
              <Td>
                <Button tone="ghost" onClick={() => setPausing(link)}>
                  {link.status === "paused" ? "Resume" : "Pause"}
                </Button>
              </Td>
            ) : null}
          </tr>
        ))}
      </Table>

      {pausing === null ? null : (
        <div class="mt-3 border-t border-line pt-3">
          <PoolLinkStatusForm
            link={pausing}
            onDone={() => {
              setPausing(null);
              onChanged();
            }}
            onCancel={() => setPausing(null)}
          />
        </div>
      )}
    </>
  );
}

function PoolLinkStatusForm({
  link,
  onDone,
  onCancel,
}: {
  link: PoolLink;
  onDone(): void;
  onCancel(): void;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const next = link.status === "paused" ? "active" : "paused";

  return (
    <MutationForm
      title={`${next === "paused" ? "Pause" : "Resume"} pool link ${link.id}`}
      description="Pausing stops the flush scheduler. The loan stays on chain exactly where it is, and a link paused mid-cycle keeps its state so resuming finishes that cycle."
      changes={[{ label: "status", before: link.status, after: next }]}
      effect="At once — the next scheduler pass reads the new status."
      submitLabel={next === "paused" ? "Pause link" : "Resume link"}
      destructive
      busy={busy}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        setBusy(true);
        setError(null);
        void adminWrites
          .setPoolLinkStatus(link.id, { status: next, reason })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <div class="sm:col-span-2 text-[11px] text-ink-muted">
        Pool {link.pool_wallet} lends {sunAmount(link.lent_sun)} to fleet {link.fleet_wallet},
        operated by {link.operator}.
      </div>
    </MutationForm>
  );
}

function RegisterWalletButton({ onDone }: { onDone(): void }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return <Button onClick={() => setOpen(true)}>Register a fund wallet</Button>;
  }
  return (
    <div class="w-full">
      <RegisterWalletForm
        onDone={() => {
          setOpen(false);
          onDone();
        }}
        onCancel={() => setOpen(false)}
      />
    </div>
  );
}

/** Register a mandated wallet. `supplier_id` is required and there is no built-in supplier. */
export function RegisterWalletForm({
  onDone,
  onCancel,
}: {
  onDone(): void;
  onCancel(): void;
}) {
  const [address, setAddress] = useState("");
  const [permissionId, setPermissionId] = useState("2");
  const [supplierId, setSupplierId] = useState("");
  const [label, setLabel] = useState("");
  const [walletRole, setWalletRole] = useState<"fleet" | "flash" | "wholesale_out" | "pool">("fleet");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  return (
    <MutationForm
      title="Register a fund wallet"
      description="The permission is verified on chain before the wallet is accepted: it must exist, include our signer key and open delegate operations only."
      changes={[
        { label: "address", before: null, after: address || "—" },
        { label: "supplier_id", before: null, after: supplierId || "—" },
        { label: "role", before: null, after: walletRole },
        { label: "permission_id", before: null, after: permissionId || "—" },
      ]}
      effect="At once. The wallet becomes inventory the allocator may sell as soon as the reconciler syncs it."
      submitLabel="Register wallet"
      busy={busy}
      disabled={address === "" || supplierId === "" || permissionId === ""}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        setBusy(true);
        setError(null);
        void adminWrites
          .registerFundWallet({
            address,
            permission_id: Number(permissionId),
            supplier_id: supplierId,
            ...(label === "" ? {} : { label }),
            role: walletRole,
            reason,
          })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <Field label="Address" error={fieldError(error, "address")}>
        <input
          class={inputClass}
          name="address"
          value={address}
          onInput={(event) => setAddress((event.currentTarget as HTMLInputElement).value.trim())}
        />
      </Field>
      <Field
        label="Permission id"
        hint="The active permission on that account, restricted to Delegate + UnDelegate."
        error={fieldError(error, "permission_id")}
      >
        <input
          class={inputClass}
          name="permission_id"
          type="number"
          min={0}
          max={255}
          value={permissionId}
          onInput={(event) => setPermissionId((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Supplier id"
        hint="Whose stake this is. Required — there is no built-in supplier anywhere in this platform."
        error={fieldError(error, "supplier_id")}
      >
        <input
          class={inputClass}
          name="supplier_id"
          value={supplierId}
          onInput={(event) => setSupplierId((event.currentTarget as HTMLInputElement).value.trim())}
        />
      </Field>
      <Field label="Role" error={fieldError(error, "role")}>
        <select
          class={inputClass}
          name="role"
          value={walletRole}
          onChange={(event) =>
            setWalletRole((event.currentTarget as HTMLSelectElement).value as typeof walletRole)
          }
        >
          <option value="fleet">fleet — delegates to customers</option>
          <option value="flash">flash</option>
          <option value="wholesale_out">wholesale_out</option>
          <option value="pool">pool — holds capital, never delegates to customers</option>
        </select>
      </Field>
      <Field label="Label (optional)" error={fieldError(error, "label")}>
        <input
          class={inputClass}
          name="label"
          value={label}
          onInput={(event) => setLabel((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
    </MutationForm>
  );
}

function CreateLinkButton({ onDone }: { onDone(): void }) {
  const [open, setOpen] = useState(false);
  if (!open) return <Button onClick={() => setOpen(true)}>Create a pool link</Button>;
  return (
    <div class="w-full">
      <CreatePoolLinkForm
        onDone={() => {
          setOpen(false);
          onDone();
        }}
        onCancel={() => setOpen(false)}
      />
    </div>
  );
}

export function CreatePoolLinkForm({
  onDone,
  onCancel,
}: {
  onDone(): void;
  onCancel(): void;
}) {
  const [poolWallet, setPoolWallet] = useState("");
  const [fleetWallet, setFleetWallet] = useState("");
  const [lentSun, setLentSun] = useState("");
  const [operator, setOperator] = useState<"platform" | "external">("platform");
  const [minInterval, setMinInterval] = useState("300");
  const [trigger, setTrigger] = useState("");
  const [budget, setBudget] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const incomplete =
    poolWallet === "" || fleetWallet === "" || lentSun === "" || trigger === "" || budget === "";

  return (
    <MutationForm
      title="Create a pool link"
      description="Both wallets must already be registered, the pool wallet in role `pool`. A platform-operated link additionally needs a verified mandate on the pool wallet."
      changes={[
        { label: "pool → fleet", before: null, after: `${poolWallet || "—"} → ${fleetWallet || "—"}` },
        { label: "lent_sun", before: null, after: lentSun || "—" },
        { label: "operator", before: null, after: operator },
        { label: "trigger_locked_bps", before: null, after: trigger || "—" },
        { label: "daily_bandwidth_budget", before: null, after: budget || "—" },
      ]}
      effect="At once, in state `reclaimed` — the loan is not on chain yet as far as the platform knows, and the next scheduler pass lends it."
      submitLabel="Create link"
      busy={busy}
      disabled={incomplete}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        setBusy(true);
        setError(null);
        void adminWrites
          .createPoolLink({
            pool_wallet: poolWallet,
            fleet_wallet: fleetWallet,
            lent_sun: lentSun,
            operator,
            min_interval_s: Number(minInterval),
            trigger_locked_bps: Number(trigger),
            daily_bandwidth_budget: Number(budget),
            reason,
          })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <Field label="Pool wallet" error={fieldError(error, "pool_wallet")}>
        <input
          class={inputClass}
          name="pool_wallet"
          value={poolWallet}
          onInput={(event) => setPoolWallet((event.currentTarget as HTMLInputElement).value.trim())}
        />
      </Field>
      <Field label="Fleet wallet" error={fieldError(error, "fleet_wallet")}>
        <input
          class={inputClass}
          name="fleet_wallet"
          value={fleetWallet}
          onInput={(event) => setFleetWallet((event.currentTarget as HTMLInputElement).value.trim())}
        />
      </Field>
      <Field label="Lent (SUN)" hint="A whole number of SUN, as a string." error={fieldError(error, "lent_sun")}>
        <input
          class={inputClass}
          name="lent_sun"
          inputMode="numeric"
          value={lentSun}
          onInput={(event) => setLentSun((event.currentTarget as HTMLInputElement).value.trim())}
        />
      </Field>
      <Field label="Operator" error={fieldError(error, "operator")}>
        <select
          class={inputClass}
          name="operator"
          value={operator}
          onChange={(event) =>
            setOperator((event.currentTarget as HTMLSelectElement).value as "platform" | "external")
          }
        >
          <option value="platform">platform — we sign the flush pairs</option>
          <option value="external">external — the counterparty's bot signs; we observe</option>
        </select>
      </Field>
      <Field
        label="Min interval (s)"
        hint="Seconds between two flushes. No default is right for every link — this is a policy."
        error={fieldError(error, "min_interval_s")}
      >
        <input
          class={inputClass}
          name="min_interval_s"
          type="number"
          min={1}
          max={86_400}
          value={minInterval}
          onInput={(event) => setMinInterval((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Trigger (bps of fleet stake locked)"
        error={fieldError(error, "trigger_locked_bps")}
      >
        <input
          class={inputClass}
          name="trigger_locked_bps"
          type="number"
          min={1}
          max={10_000}
          value={trigger}
          onInput={(event) => setTrigger((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Daily bandwidth budget"
        hint="One flush costs 566 bandwidth. The scheduler stops and alerts when the budget is spent."
        error={fieldError(error, "daily_bandwidth_budget")}
      >
        <input
          class={inputClass}
          name="daily_bandwidth_budget"
          type="number"
          min={0}
          value={budget}
          onInput={(event) => setBudget((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
    </MutationForm>
  );
}

function JustLendPanel({ view }: { view: JustLendView }) {
  if (view.data.length === 0 && view.recent_settled.length === 0) {
    return (
      <EmptyState>
        No JustLend rental is open and none has settled. Orders reach JustLend only when own capacity
        and every provider are short.
      </EmptyState>
    );
  }
  return (
    <div class="space-y-3">
      <dl class="grid gap-x-6 gap-y-1 text-[12px] sm:grid-cols-4">
        <div>
          <dt class="num text-[10.5px] tracking-[0.08em] text-ink-muted uppercase">Open</dt>
          <dd class="num mt-0.5 text-[16px] text-ink">{count(view.summary.open)}</dd>
        </div>
        <div>
          <dt class="num text-[10.5px] tracking-[0.08em] text-ink-muted uppercase">Overdue</dt>
          <dd class={`num mt-0.5 text-[16px] ${view.summary.overdue > 0 ? "text-warning" : "text-ink"}`}>
            {count(view.summary.overdue)}
          </dd>
        </div>
        <div>
          <dt class="num text-[10.5px] tracking-[0.08em] text-ink-muted uppercase">Refunds unread</dt>
          <dd class="num mt-0.5 text-[16px] text-ink">{count(view.summary.unsettled_returns)}</dd>
        </div>
        <div>
          <dt class="num text-[10.5px] tracking-[0.08em] text-ink-muted uppercase">Prepaid, open</dt>
          <dd class="num mt-0.5 text-[16px] text-ink">
            {view.summary.open_prepaid_sun === null ? <Blank title="Not every open rental carries a prepay figure" /> : trx(view.summary.open_prepaid_sun)}
          </dd>
        </div>
      </dl>
      <RentalTable rows={[...view.data, ...view.recent_settled]} empty="No rental is open." />
    </div>
  );
}

function RentalTable({ rows, empty }: { rows: JustLendRental[]; empty: string }) {
  return (
    <div class="pt-1">
      <Table
        rows={rows.length}
        empty={empty}
        head={
          <tr>
            <Th>Order</Th>
            <Th>Receiver</Th>
            <Th>State</Th>
            <Th numeric>Amount</Th>
            <Th numeric>Prepaid</Th>
            <Th numeric>Final cost</Th>
            <Th>Expires</Th>
            <Th>Return tx</Th>
          </tr>
        }
      >
        {rows.map((row) => (
          <tr key={row.allocation_id}>
            <Td mono>
              <ShortId value={row.order_id} />
            </Td>
            <Td mono>
              <Address value={row.receiver} />
            </Td>
            <Td>
              <span class="inline-flex gap-1">
                <Pill value={row.state} />
                {row.overdue ? <Pill value="overdue" /> : null}
                {row.unsettled ? <Pill value="refund unread" tone="attention" /> : null}
              </span>
            </Td>
            <Td numeric>{count(row.amount_requested)}</Td>
            <Td numeric>{row.prepaid_sun === null ? <Blank /> : trx(row.prepaid_sun)}</Td>
            <Td numeric>{row.final_cost_sun === null ? <Blank title="Known only once the return is read" /> : trx(row.final_cost_sun)}</Td>
            <Td class={row.overdue ? "text-warning" : ""}>{utc(row.expires_at)}</Td>
            <Td mono>{row.return_txid === null ? <Blank /> : <TxId value={row.return_txid} />}</Td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
